<template>
  <div class="divide-y divide-gray-50 dark:divide-gray-800/60">
    <div v-for="(row, idx) in rows" :key="row.key ?? idx"
      class="relative px-5 py-2.5 flex items-center gap-3 group">
      <!-- The bar sits behind the text rather than beside it. A separate bar
           column costs horizontal space that the labels need, and the relative
           lengths are still readable as a background. -->
      <span class="absolute inset-y-1 left-1 rounded-md pointer-events-none transition-[width] duration-500"
        :style="{ width: `calc(${barWidth(row)}% - 0.5rem)`, backgroundColor: barColor(row, idx) }"></span>

      <span v-if="row.swatch" class="relative w-2.5 h-2.5 rounded-full flex-shrink-0"
        :style="{ backgroundColor: row.swatch }"></span>
      <span v-else-if="row.emoji" class="relative text-base leading-none flex-shrink-0">{{ row.emoji }}</span>

      <span class="relative text-sm font-medium text-gray-800 dark:text-gray-100 truncate flex-1 min-w-0"
        :title="row.label">{{ row.label }}</span>

      <span v-if="row.meta" class="relative text-[11px] text-gray-400 dark:text-gray-500 tabular-nums flex-shrink-0 hidden sm:inline">
        {{ row.meta }}
      </span>
      <span v-if="row.badge" class="relative text-[11px] font-semibold px-1.5 py-0.5 rounded flex-shrink-0"
        :class="row.badgeClass || 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'">
        {{ row.badge }}
      </span>
      <span class="relative text-sm font-bold text-gray-900 dark:text-white tabular-nums flex-shrink-0 w-12 text-right">
        {{ row.value }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  rows:  { type: Array, required: true },   // { key, label, value, weight, swatch, emoji, meta, badge, badgeClass }
  color: { type: String, default: '#2563eb' },
})

// Scaled against the largest row rather than the total, so a list where one
// entry holds 90% of the volume still shows the shape of the tail.
const max = computed(() => Math.max(1, ...props.rows.map(r => Number(r.weight ?? r.value) || 0)))

function barWidth(row) {
  return Math.max(2, ((Number(row.weight ?? row.value) || 0) / max.value) * 100)
}

function barColor(row, idx) {
  const base = row.swatch || props.color
  // Fades down the list so rank is legible without numbering every row.
  const alpha = Math.max(0.06, 0.16 - idx * 0.012)
  return hexToRgba(base, alpha)
}

function hexToRgba(hex, alpha) {
  const clean = String(hex).replace('#', '')
  if (clean.length !== 6) return `rgba(37, 99, 235, ${alpha})`
  const n = parseInt(clean, 16)
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
}
</script>
